// ============================================================
//  HOOK: usePdfLibrary (BACKEND)
//  - Lee PDFs desde tu API (/api/pdfs)
//  - Aplica filtros (texto, cliente, tipo, mes)
//  - Agrupa por mes
//  - Expone contadores + opciones para selects
// ============================================================

import { useState, useEffect, useMemo, useCallback } from 'react';

// ============================================================
// ✅ ENDPOINT ROBUSTO (no duplica /api y sirve en prod/dev)
// Recomendado:
//   VITE_API_BASE=https://elektroniks-backend.onrender.com
// o:
//   VITE_API_BASE=https://elektroniks-backend.onrender.com/api
// ============================================================
const RAW_BASE = import.meta.env.VITE_API_BASE || '';
const API_BASE = RAW_BASE.replace(/\/+$/, '');

const PDF_ENDPOINT = API_BASE.endsWith('/api')
    ? `${API_BASE}/pdfs`
    : `${API_BASE}/api/pdfs`;

// Helpers pequeños
const safeClient = (client) => {
    if (!client || !client.trim()) return 'Sin cliente';
    return client.trim();
};

const getTypeLabel = (type) => {
    if (!type) return 'Sin tipo';
    if (type === 'work-order') return 'Orden de trabajo';
    if (type === 'service-report') return 'Reporte de trabajo';
    if (type === 'budget') return 'Presupuesto';
    return type;
};

const getMonthKeyAndLabel = (createdAt) => {
    const buildFromDate = (dateObj) => {
        const key = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
        const label = dateObj.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
        return { key, label };
    };

    if (!createdAt) return buildFromDate(new Date());

    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return buildFromDate(new Date());

    return buildFromDate(d);
};

export const usePdfLibrary = () => {
    const [allPdfs, setAllPdfs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Filtros de UI
    const [searchText, setSearchText] = useState('');
    const [clientFilter, setClientFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [monthFilter, setMonthFilter] = useState('all');

    // --------------------------------------------------------
    // Fetch: lista desde backend
    // --------------------------------------------------------
    const fetchPdfs = useCallback(async () => {
        setIsLoading(true);

        try {
            const res = await fetch(PDF_ENDPOINT, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => '');
                throw new Error(`GET ${PDF_ENDPOINT} failed: ${res.status} ${txt}`);
            }

            const json = await res.json();

            // ✅ Soporta:
            // 1) { success:true, data:[...] }
            // 2) [...] (por si algún día cambias backend)
            const list = Array.isArray(json)
                ? json
                : Array.isArray(json?.data)
                    ? json.data
                    : [];

            setAllPdfs(list);
        } catch (err) {
            console.error('[usePdfLibrary] fetchPdfs error:', err);
            setAllPdfs([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Carga inicial
    useEffect(() => {
        fetchPdfs();
    }, [fetchPdfs]);

    // --------------------------------------------------------
    // Filtros aplicados sobre la lista
    // --------------------------------------------------------
    const filteredPdfs = useMemo(() => {
        if (allPdfs.length === 0) return [];
        const text = searchText.trim().toLowerCase();

        return allPdfs.filter((pdf) => {
            if (!pdf) return false;

            // Filtro por texto
            if (text) {
                const hayCoincidencia =
                    (pdf.name || '').toLowerCase().includes(text) ||
                    safeClient(pdf.client).toLowerCase().includes(text) ||
                    (pdf.folio || '').toLowerCase().includes(text) ||
                    getTypeLabel(pdf.type).toLowerCase().includes(text) ||
                    (pdf.source || '').toLowerCase().includes(text);

                if (!hayCoincidencia) return false;
            }

            // Filtro por cliente
            if (clientFilter !== 'all') {
                if (safeClient(pdf.client) !== clientFilter) return false;
            }

            // Filtro por tipo
            if (typeFilter !== 'all') {
                if ((pdf.type || '') !== typeFilter) return false;
            }

            // Filtro por mes
            if (monthFilter !== 'all') {
                const { key } = getMonthKeyAndLabel(pdf.createdAt);
                if (key !== monthFilter) return false;
            }

            return true;
        });
    }, [allPdfs, searchText, clientFilter, typeFilter, monthFilter]);

    // --------------------------------------------------------
    // Agrupación por mes
    // --------------------------------------------------------
    const groupedByMonth = useMemo(() => {
        if (filteredPdfs.length === 0) return [];

        const groupsMap = new Map();

        filteredPdfs.forEach((pdf) => {
            const { key, label } = getMonthKeyAndLabel(pdf.createdAt);

            if (!groupsMap.has(key)) {
                groupsMap.set(key, { key, label, items: [] });
            }
            groupsMap.get(key).items.push(pdf);
        });

        const groups = Array.from(groupsMap.values());

        // Orden por mes descendente
        groups.sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));

        // Orden interno por fecha desc
        groups.forEach((g) => {
            g.items.sort((a, b) => {
                const da = new Date(a.createdAt).getTime();
                const db = new Date(b.createdAt).getTime();
                return db - da;
            });
        });

        return groups;
    }, [filteredPdfs]);

    // --------------------------------------------------------
    // Contadores
    // --------------------------------------------------------
    const totalCount = allPdfs.length;
    const totalFiltered = filteredPdfs.length;

    const totalWorkOrders = useMemo(
        () => filteredPdfs.filter((pdf) => pdf.type === 'work-order').length,
        [filteredPdfs]
    );

    const totalBudgets = useMemo(
        () => filteredPdfs.filter((pdf) => pdf.type === 'budget').length,
        [filteredPdfs]
    );

    const totalServiceReports = useMemo(
        () => filteredPdfs.filter((pdf) => pdf.type === 'service-report').length,
        [filteredPdfs]
    );

    // --------------------------------------------------------
    // Opciones selects
    // --------------------------------------------------------
    const clientOptions = useMemo(() => {
        const set = new Set();
        allPdfs.forEach((pdf) => {
            const c = safeClient(pdf.client);
            if (c !== 'Sin cliente') set.add(c);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
    }, [allPdfs]);

    const typeOptions = useMemo(() => {
        const set = new Set();
        allPdfs.forEach((pdf) => {
            if (pdf?.type) set.add(pdf.type);
        });
        return Array.from(set);
    }, [allPdfs]);

    const monthOptions = useMemo(() => {
        const map = new Map();
        allPdfs.forEach((pdf) => {
            const { key, label } = getMonthKeyAndLabel(pdf.createdAt);
            if (!map.has(key)) map.set(key, { value: key, label });
        });

        const arr = Array.from(map.values());
        arr.sort((a, b) => (a.value < b.value ? 1 : a.value > b.value ? -1 : 0));
        return arr;
    }, [allPdfs]);

    // --------------------------------------------------------
    // Acciones (BACKEND)
    // --------------------------------------------------------
    const handleDeletePdf = useCallback(
        async (id) => {
            if (!id) return;
            if (!window?.confirm?.('¿Seguro que deseas eliminar este PDF del historial?')) return;

            try {
                // ✅ FIX: si el id trae "/" (ej. S/N), hay que escaparlo
                const safeId = encodeURIComponent(id);

                const res = await fetch(`${PDF_ENDPOINT}/${safeId}`, { method: 'DELETE' });

                if (!res.ok) {
                    const txt = await res.text().catch(() => '');
                    throw new Error(`DELETE ${PDF_ENDPOINT}/${safeId} failed: ${res.status} ${txt}`);
                }

                fetchPdfs();
            } catch (err) {
                console.error('[usePdfLibrary] delete error:', err);
                alert('No se pudo eliminar el documento.');
            }
        },
        [fetchPdfs]
    );

    const handleClearAll = useCallback(async () => {
        if (!window?.confirm?.('Vas a limpiar TODO el historial de PDFs en la BD. ¿Seguro?')) return;

        try {
            const res = await fetch(PDF_ENDPOINT, { method: 'DELETE' });

            if (!res.ok) {
                const txt = await res.text().catch(() => '');
                throw new Error(`DELETE ${PDF_ENDPOINT} failed: ${res.status} ${txt}`);
            }

            fetchPdfs();
        } catch (err) {
            console.error('[usePdfLibrary] clearAll error:', err);
            alert('No se pudo limpiar el historial.');
        }
    }, [fetchPdfs]);

    // ✅ Editar (usa meta.rawData)
    const handleEditPdf = useCallback((pdf, onEdit) => {
        if (!pdf) return;

        const raw = pdf?.meta?.rawData || pdf?.rawData || pdf?.data || null;

        if (!raw) {
            window?.alert?.(
                'Este PDF no tiene datos para editar (rawData). Genera uno nuevo para que quede editable.'
            );
            return;
        }

        if (typeof onEdit === 'function') onEdit(raw);
    }, []);

    return {
        groupedByMonth,

        totalCount,
        totalFiltered,
        totalWorkOrders,
        totalServiceReports,
        totalBudgets,

        searchText,
        setSearchText,
        clientFilter,
        setClientFilter,
        clientOptions,
        typeFilter,
        setTypeFilter,
        typeOptions,
        monthFilter,
        setMonthFilter,
        monthOptions,

        handleDeletePdf,
        handleClearAll,
        handleEditPdf,

        isLoading,
        refetch: fetchPdfs,
    };
};
